import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { raiseClientTicket, raiseProjectTicket } from '../../../Redux/actions';
import { THEME_COLOR, buttonStyles, inputLabelFocusedStyle, selectFocusedStyle } from '../../../theme/formStyles';
import { PRIORITY_STYLE } from './TicketStageTracker';

const fieldSx = { '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME_COLOR }, '& .MuiInputLabel-root.Mui-focused': { color: THEME_COLOR } };
export const EQUIPMENT = ['Flowmeter', 'Level sensor', 'Water quality sensor', 'Energy meter', 'Gateway / connectivity', 'Dashboard / app', 'Other'];

// Report a problem at the facility. Clients raise their own; the team can raise one on a client's behalf.
export default function RaiseTicketDialog({ facilityId, projectId, forClient = false, onClose, onRaised }) {
    const dispatch = useDispatch();
    const [form, setForm] = useState({ title: '', equipment: '', priority: 'Medium', details: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const submit = () => {
        if (form.title.trim().length < 3) { setError('Say briefly what the problem is'); return; }
        setSaving(true); setError(null);
        const body = { ...form, project_id: projectId || null };
        dispatch((forClient ? raiseClientTicket : raiseProjectTicket)(facilityId, body)).then((res) => {
            setSaving(false);
            if (res?.data?.success) onRaised(res.data.data, res.data.message);
            else setError(res?.data?.message || 'Could not raise the ticket');
        });
    };

    return (
        <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4 } }}>
            <DialogTitle sx={{ fontWeight: 700 }}>🎫 Raise a ticket</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, pt: 1 }}>
                    <TextField label="What's the problem? *" value={form.title} onChange={e => set('title', e.target.value)} inputProps={{ maxLength: 200 }} sx={fieldSx} autoFocus />
                    <FormControl>
                        <InputLabel sx={inputLabelFocusedStyle}>Equipment</InputLabel>
                        <Select label="Equipment" value={form.equipment} onChange={e => set('equipment', e.target.value)} sx={selectFocusedStyle} MenuProps={{ disableScrollLock: true }}>
                            <MenuItem value=""><em>Not sure</em></MenuItem>
                            {EQUIPMENT.map(x => <MenuItem key={x} value={x}>{x}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Box>
                        <Box sx={{ fontSize: '12.5px', color: '#6b7785', mb: 0.5 }}>Priority</Box>
                        <ToggleButtonGroup exclusive size="small" value={form.priority} onChange={(e, v) => v && set('priority', v)}>
                            {Object.keys(PRIORITY_STYLE).map(p => (
                                <ToggleButton key={p} value={p} sx={{ textTransform: 'none', px: 2, '&.Mui-selected': { ...PRIORITY_STYLE[p], fontWeight: 700 } }}>{p}</ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>
                    <TextField label="Details" multiline minRows={3} value={form.details} onChange={e => set('details', e.target.value)} inputProps={{ maxLength: 2000 }}
                        placeholder="When did it start, where on site, what you see on the dashboard…" sx={fieldSx} />
                    {error && <Alert severity="error">{error}</Alert>}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant="outlined" onClick={onClose} disabled={saving} sx={buttonStyles.outlined}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={saving} sx={buttonStyles.contained}>{saving ? <CircularProgress size={20} color="inherit" /> : 'Raise ticket'}</Button>
            </DialogActions>
        </Dialog>
    );
}
